import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Print-only KPI row.
//
// Lays the metrics out as a row of cards above the map card, where the
// on-screen header (SimpleKPI) shows one solo value plus a strip of buttons.
//
// The cards carry SimpleKPI's own .hv-hh-card classes rather than a private set:
// those rules sit outside every @media block, so they apply to the printed sheet
// unchanged and the two KPI blocks cannot drift apart visually. The only thing
// that differs is the element — <div> here, <button> there — because print is
// display-only and a button would advertise an interaction the sheet cannot
// offer. .hv-hh-card sets font-family: inherit for the button case, which a div
// gets for free, so no style depends on the tag.
//
// Metric selection lives in SimpleKPI and arrives here as
// dashboardState.selectedMetricId — this component never calls back into the
// dashboard, so the two KPI blocks cannot fight over which metric is active.
// Values come from the same useSelectionData/resolveMetricValue path SimpleKPI
// and the narrative panel use, so the printed numbers always match the screen.
import React from 'react';
import { useSelectionData, resolveMetricValue } from '../../services/useSelectionData';
import { getMetrics, getMetricLabel } from '../../services/metricRegistry';
const PrintSimpleKPI = ({ dashboardState }) => {
    // County/state rows for the current selection — cached, so sharing the hook
    // with SimpleKPI costs no extra requests.
    const { countyData, stateData, isLoading } = useSelectionData(dashboardState);
    // Available metric descriptors for the current category/geo level
    const metrics = dashboardState.selectedCategoryId
        ? getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel)
        : [];
    // Formatting rules ($/% affixes, decimals, shortage level labels, suppression
    // sentinels) all live in resolveMetricValue.
    const displayValue = (metric) => {
        if (!dashboardState.selectedCategoryId) {
            return 'N/A';
        }
        if (isLoading) {
            return '...';
        }
        return resolveMetricValue(dashboardState, countyData, stateData, metric.id).formatted;
    };
    // Nothing to print until a category resolves to metrics. The screen already
    // reports that state in the map card's header, so print stays silent.
    if (metrics.length === 0) {
        return null;
    }
    // Which card prints as selected. Guards the brief window before SimpleKPI's
    // auto-select effect has pushed a metric id into dashboard state.
    const activeMetricId = metrics.some(m => m.id === dashboardState.selectedMetricId)
        ? dashboardState.selectedMetricId
        : metrics[0].id;
    // The .print-only wrapper is a separate element from the card row: the print
    // rule forces `display: block`, which would collapse .hv-hh-btns' flex layout
    // and stack every card full width.
    return (_jsx("div", { className: "print-indicators print-only", id: "kpi-row-print", children: _jsx("div", { className: "hv-hh-btns", id: "kpi-row", role: "group", "aria-label": "Key performance indicators", children: metrics.map((metric) => {
                const isSelected = activeMetricId === metric.id;
                const value = displayValue(metric);
                const label = getMetricLabel(metric.id, dashboardState.geoLevel);
                // .active is the same selected treatment SimpleKPI's chips use:
                // tinted fill, accent border, accent value.
                return (_jsxs("div", { className: `hv-hh-card${isSelected ? ' active' : ''}`, children: [_jsx("span", { className: "cv", children: value }), _jsx("span", { className: "fw-semibold small text-wrap text-gray-600", children: label })] }, metric.id));
            }) }) }));
};
export default PrintSimpleKPI;
